import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import api from '../../../api/axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    Sparkles, Check, X, Pencil, Loader2, Copy, ExternalLink, AlertTriangle, Search, Send, Linkedin, UserPlus, RefreshCw, RotateCcw,
} from 'lucide-react';

import ContactCountPicker from './ContactCountPicker';
import {
    updateAction, markActionSent, draftAction, sendActionNow, refreshDigest, DIGEST_ACTION_STATUSES,
} from '../../../redux/DigestSlice/digestSlice';
import { sendConnectionRequest, fetchMyInviteStatuses } from '../../../redux/LIAuthSlice/liAuthSlice';
import {
    toneFor, rowIdentityFor, rowNeedsContacts, isTerminal, tileColorFor, initialsFor,
    BLOCKED_BANNERS, kindMetaFor, navigateToOpportunity, publicIdentifierFrom,
    statusLabelFor, sendButtonLabelFor, sendHintFor, priorEmailLabelFor,
    googleConnectUrl,
} from './digestHelpers';

/**
 * The detail for one queue row, held in a fixed pane beside the list.
 *
 * A fetch_contact row and a message row are genuinely different decisions —
 * one buys contact details, the other sends words in the user's name — so the
 * body of this pane branches rather than pretending they share a shape.
 */
const DigestDetailPane = ({ digestId, action, position, total, senderEmail }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [drafting, setDrafting] = useState(false);
    const [inviting, setInviting] = useState(false);
    const [refinePrompt, setRefinePrompt] = useState('');
    const [invited, setInvited] = useState(false);
    const linkedInAccount = useSelector((st) => st.liauth?.isLinkedInConnected);
    const inviteStatusesByPublicId = useSelector((st) => st.liauth?.inviteStatusesByPublicId);

    /**
     * Whether an invite already went to this person, from the SHARED cache the
     * rest of the app writes — not local state. Local state forgets the moment
     * the pane moves to another row, so the button offered to re-invite people
     * who were already invited (from here, from My Network, or last week).
     */
    const publicId = publicIdentifierFrom(action?.contact?.linkedinUrl);
    const priorEmailLabel = priorEmailLabelFor(action);

    /**
     * An unsendable channel gets a way OUT of that state, not just a notice.
     *
     * The blocked banner named a CTA and an href and rendered neither, so a
     * user whose Gmail was not connected saw "Gmail is not connected." above a
     * live "Send Email now" button that could only fail. My Network already
     * offered "Connect Google to send" here; the digest now does the same, via
     * the direct consent URL rather than the /profile bounce.
     */
    const connectGoogle = () => {
        const url = googleConnectUrl({
            baseURL: api.defaults.baseURL || process.env.REACT_APP_API_BASE_URL || window.location.origin,
            userEmail: senderEmail,
            origin: window.location.origin
        });
        if (url) window.open(url, '_self');
    };
    const knownInviteStatus = publicId ? (inviteStatusesByPublicId || {})[publicId] : null;
    const alreadyInvited = invited || Boolean(knownInviteStatus);
    const [editing, setEditing] = useState(false);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');

    const needs = rowNeedsContacts(action);
    const terminal = isTerminal(action);

    // Re-seed the editor whenever the pane points at a different row, so an
    // abandoned edit never leaks its text onto the next opportunity.
    useEffect(() => {
        setEditing(false);
        setSubject(action?.draft?.subject || '');
        setBody(action?.draft?.body || '');
        // Local only — a new row has not been invited just because the last was.
        setInvited(false);
        setRefinePrompt('');
    }, [action?.actionId, action?.draft?.subject, action?.draft?.body]);

    // The invite cache is shared across the app; load it once so this pane
    // knows about invites sent from My Network too.
    useEffect(() => {
        if (linkedInAccount?.accountId) dispatch(fetchMyInviteStatuses());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkedInAccount?.accountId]);

    /**
     * Drafting is lazy — the digest plans up to 100 rows and writes bodies for
     * none of them, because doing so would cost minutes and a real model bill
     * for rows nobody opens. Opening a row is what pays for its draft.
     */
    const isPending = Boolean(action?.draft?.pending);
    useEffect(() => {
        if (!action || !digestId || !isPending || drafting) return;
        setDrafting(true);
        dispatch(draftAction({ digestId, actionId: action.actionId }))
            .finally(() => setDrafting(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action?.actionId, isPending, digestId]);

    if (!action) {
        return (
            <div className="cc-card" style={{ padding: '48px 20px', textAlign: 'center' }}>
                <p style={{ fontSize: 13, fontWeight: 700 }}>Select an opportunity</p>
                <p className="cc-tiny" style={{ marginTop: 4 }}>Pick a row from the queue to review it here.</p>
            </div>
        );
    }

    const tone = toneFor(action.source?.type);
    const { title, meta } = rowIdentityFor(action);
    const kindMeta = kindMetaFor(action.kind);
    const blocked = !action.channelAvailable ? BLOCKED_BANNERS[action.channelBlockedReason] : null;
    // Declared AFTER `blocked`, not beside connectGoogle: `const` is in the
    // temporal dead zone until its own line, so reading it earlier threw on
    // every render and took the whole page down with it.
    const needsGoogle = Boolean(blocked) && action.kind === 'email';

    const setStatus = async (status) => {
        if (busy || terminal) return;
        setBusy(true);
        await dispatch(updateAction({ digestId, actionId: action.actionId, status }));
        setBusy(false);
    };

    const saveDraft = async () => {
        setBusy(true);
        await dispatch(updateAction({ digestId, actionId: action.actionId, draft: { subject, body } }));
        setBusy(false);
        setEditing(false);
    };

    /**
     * Rewrite this draft against an instruction, the way My Network's modal
     * does. The current text travels with it so the model edits what is on
     * screen rather than starting from scratch.
     */
    const runRefine = async (prompt) => {
        const instruction = String(prompt || '').trim();
        if (!instruction || drafting) return;
        setDrafting(true);
        await dispatch(draftAction({ digestId, actionId: action.actionId, userPrompt: instruction }));
        setDrafting(false);
        setRefinePrompt('');
    };

    const copyDraft = async () => {
        const text = [subject && `Subject: ${subject}`, body].filter(Boolean).join('\n\n');
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Draft copied.');
        } catch (_) {
            toast.error('Could not copy — select the text and copy manually.');
        }
    };

    return (
        <div className="cc-card" style={{ overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--cc-divider)', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="cc-logo-tile" style={{ background: tileColorFor(action.contact?.companyName || title) }}>
                    {initialsFor(action.contact?.companyName || title)}
                </div>
                <div className="cc-grow" style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
                        <span className="cc-trunc" style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>{title}</span>
                        {/* The recipient's own address, beside their name — the only
                            other email on this screen is the SENDER's, and having
                            just that one visible made it ambiguous who a message
                            was actually going to. Click to copy. */}
                        {action.contact?.email && (
                            <button
                                type="button"
                                className="cc-trunc"
                                title={`Copy ${action.contact.email}`}
                                onClick={async () => {
                                    try {
                                        await navigator.clipboard.writeText(action.contact.email);
                                        toast.success('Email address copied.');
                                    } catch (_) {
                                        toast.error('Could not copy the address.');
                                    }
                                }}
                                style={{
                                    border: 0, background: 'transparent', padding: 0, cursor: 'pointer',
                                    font: 'inherit', fontSize: 12, fontWeight: 500,
                                    color: 'var(--cc-primary)', maxWidth: 260, flexShrink: 1,
                                }}
                            >
                                {action.contact.email}
                            </button>
                        )}
                    </div>
                    <div className="cc-trunc cc-tiny" style={{ marginTop: 2 }}>{meta}</div>
                </div>
                <span className="cc-chip" style={{ background: tone.bg, color: tone.fg, fontWeight: 700 }}>{tone.label}</span>
                {action.status !== 'proposed' && (
                    <span className="cc-chip cc-chip-neutral">{statusLabelFor(action)}</span>
                )}
                <span className="cc-tiny" style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{position} of {total}</span>
            </div>

            <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ padding: '12px 14px', borderRadius: 10, background: '#FAFBFD', border: '1px solid var(--cc-divider)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <Sparkles style={{ width: 13, height: 13, color: 'var(--cc-primary)' }} />
                        <span className="cc-eyebrow" style={{ color: 'var(--cc-primary)' }}>Why now</span>
                        <span style={{ flex: 1 }} />
                        {(action.opportunity?.jobId || action.opportunity?.signalId) && (
                            <button
                                type="button"
                                className="cc-btn cc-btn-ghost cc-btn-sm"
                                onClick={() => navigateToOpportunity(action, navigate)}
                            >
                                <ExternalLink style={{ width: 11, height: 11 }} />Open source
                            </button>
                        )}
                    </div>
                    <div className="cc-body" style={{ marginTop: 7 }}>
                        {action.source?.whyNow || 'No specific trigger recorded for this opportunity.'}
                    </div>
                </div>

                {/*
                    The person's LinkedIn, on every row that has one.
                    Reuses My Network's invite-from-url path rather than a second
                    implementation: it resolves a stale providerId from the URL
                    and records an Invitation, which is what the rolling 7-day
                    invite budget counts.
                */}
                {action.contact?.linkedinUrl && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, background: '#F5F9FF', border: '1px solid #D6E4F7', flexWrap: 'wrap' }}>
                        <Linkedin style={{ width: 15, height: 15, color: '#0A66C2', flexShrink: 0 }} />
                        <a
                            href={action.contact.linkedinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="cc-trunc"
                            style={{ fontSize: 12.5, fontWeight: 600, color: '#0A66C2', textDecoration: 'none', maxWidth: 320 }}
                            title={action.contact.linkedinUrl}
                        >
                            {String(action.contact.linkedinUrl).replace(/^https?:\/\/(www\.)?linkedin\.com/, 'linkedin.com')}
                        </a>
                        <span style={{ flex: 1 }} />
                        {alreadyInvited ? (
                            <span className={`cc-chip ${knownInviteStatus === 'accepted' ? 'cc-chip-success' : 'cc-chip-warn'}`}>
                                <Check style={{ width: 11, height: 11 }} />
                                {knownInviteStatus === 'accepted' ? 'Connected' : 'Invite sent'}
                            </span>
                        ) : (
                            <button
                                type="button"
                                className="cc-btn cc-btn-secondary cc-btn-sm"
                                disabled={inviting || !linkedInAccount?.accountId}
                                title={!linkedInAccount?.accountId ? 'Connect LinkedIn first' : 'Send a connection invite'}
                                onClick={async () => {
                                    setInviting(true);
                                    const r = await dispatch(sendConnectionRequest({
                                        profileUrl: action.contact.linkedinUrl,
                                        accountId: linkedInAccount.accountId,
                                        contactId: action.contact.contactId,
                                        providerId: action.contact.providerId
                                    }));
                                    setInviting(false);
                                    if (sendConnectionRequest.fulfilled.match(r)) {
                                        setInvited(true);
                                        toast.success('LinkedIn invite sent.');
                                        // The invite is recorded by My Network's endpoint, not
                                        // the digest, so re-read it for the row's "Invite sent".
                                        dispatch(refreshDigest(digestId));
                                    } else {
                                        toast.error(r.payload || 'Could not send the invite.');
                                    }
                                }}
                            >
                                {inviting
                                    ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" />
                                    : <UserPlus style={{ width: 12, height: 12 }} />}
                                Send LinkedIn invite
                            </button>
                        )}
                    </div>
                )}

                {action.error && (
                    <div style={{ display: 'flex', gap: 8, padding: '10px 12px', borderRadius: 10, background: '#FEF2F2', border: '1px solid #FECACA' }}>
                        <AlertTriangle style={{ width: 14, height: 14, color: '#B91C1C', flexShrink: 0, marginTop: 1 }} />
                        <span className="cc-tiny" style={{ color: '#B91C1C' }}>{action.error}</span>
                    </div>
                )}

                {needs ? (
                    /* A fetch row has nobody to write to yet — the decision is how
                       many contacts to buy, not what to say. */
                    <div>
                        <div className="cc-eyebrow">How many contacts to find</div>
                        <div style={{ marginTop: 8 }}>
                            <ContactCountPicker digestId={digestId} action={action} disabled={terminal} />
                        </div>
                        <div className="cc-tiny" style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Search style={{ width: 12, height: 12 }} />
                            Contacts are found when you press Execute. You are charged only for contacts actually returned.
                        </div>
                        {action.ownedAtCompany > 0 && (
                            /* Stated before approval, because a fetch at a company
                               already bought from may return people you have. It
                               costs nothing extra — the executor skips owned
                               contacts — but it may also deliver nothing new. */
                            <div style={{ marginTop: 10, display: 'flex', gap: 8, padding: '10px 12px', borderRadius: 10, background: '#FFFBEB', border: '1px solid #FDE68A' }}>
                                <AlertTriangle style={{ width: 13, height: 13, color: '#B45309', flexShrink: 0, marginTop: 1 }} />
                                <span className="cc-tiny" style={{ color: '#B45309' }}>
                                    You already have <strong>{action.ownedAtCompany}</strong> contact{action.ownedAtCompany === 1 ? '' : 's'} at{' '}
                                    {action.contact?.companyName || 'this company'}. Anyone already yours is skipped and not charged,
                                    so this may return fewer than {action.contactsRequested || 0} new people.
                                </span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        {/*
                            The header names the draft; the chip names its state.

                            Every contact with an address now gets an email row,
                            so this reads "Outreach draft · Email" across the
                            whole list instead of one card breaking rank with
                            "· LinkedIn invite". Whether it has been sent is a
                            separate fact, and it belongs in a chip beside the
                            heading rather than replacing it.
                        */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div className="cc-eyebrow cc-grow">Outreach draft · {kindMeta.label}</div>
                            {priorEmailLabel && (
                                <span
                                    className="cc-chip cc-chip-success"
                                    title={`You emailed ${action.contact?.name || 'this contact'} on ${new Date(action.emailSentAt).toLocaleDateString()}`}
                                >
                                    <Check style={{ width: 11, height: 11 }} />
                                    {priorEmailLabel}
                                </span>
                            )}
                            {action.draft?.fallback && (
                                <span className="cc-chip cc-chip-warn">Template fallback</span>
                            )}
                            {action.draft?.edited && <span className="cc-chip cc-chip-neutral">Edited by you</span>}
                        </div>

                        <div style={{ marginTop: 8, border: '1px solid var(--cc-border)', borderRadius: 10, overflow: 'hidden' }}>
                            {action.kind === 'email' && (
                                <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--cc-divider)', display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span className="cc-tiny" style={{ fontWeight: 600, width: 52, flexShrink: 0 }}>Subject</span>
                                    {editing ? (
                                        <input
                                            value={subject}
                                            onChange={(e) => setSubject(e.target.value)}
                                            style={{ flex: 1, border: 0, outline: 'none', font: 'inherit', fontSize: 13, fontWeight: 600, color: 'var(--cc-ink-900)', background: 'transparent' }}
                                        />
                                    ) : isPending || drafting ? (
                                        /* The subject is written by the same call as
                                           the body, so it must show the same wait.
                                           An em dash beside a spinning body read as
                                           "this one came back empty" rather than
                                           "this one has not arrived yet". */
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--cc-ink-500)' }}>
                                            <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" />
                                            <span className="cc-tiny">Writing the subject line…</span>
                                        </span>
                                    ) : (
                                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--cc-ink-900)' }}>{subject || '—'}</span>
                                    )}
                                </div>
                            )}

                            {editing ? (
                                <textarea
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    rows={7}
                                    style={{ width: '100%', border: 0, outline: 'none', resize: 'vertical', padding: '12px 14px', font: 'inherit', fontSize: 13, lineHeight: 1.6, color: 'var(--cc-ink-700)' }}
                                />
                            ) : isPending || drafting ? (
                                <div style={{ padding: '18px 14px', display: 'flex', alignItems: 'center', gap: 8, color: 'var(--cc-ink-500)' }}>
                                    <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
                                    <span className="cc-tiny">Writing your draft from your profile…</span>
                                </div>
                            ) : (
                                <div style={{ padding: '12px 14px', fontSize: 13, lineHeight: 1.6, color: 'var(--cc-ink-700)', whiteSpace: 'pre-wrap' }}>
                                    {body || 'No draft was generated for this row.'}
                                </div>
                            )}

                            <div style={{ padding: '9px 14px', borderTop: '1px solid var(--cc-divider)', background: '#FAFBFD', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                {/*
                                    Rewrite forces a fresh generation even though
                                    the row already has a body. Without it a draft
                                    written by an older prompt is frozen forever:
                                    the draft endpoint skips anything not pending.
                                */}
                                {!editing && (
                                    <button
                                        type="button"
                                        className="cc-btn cc-btn-ghost cc-btn-sm"
                                        disabled={busy || drafting}
                                        title="Generate this draft again"
                                        onClick={async () => {
                                            setDrafting(true);
                                            await dispatch(draftAction({ digestId, actionId: action.actionId, force: true }));
                                            setDrafting(false);
                                        }}
                                    >
                                        {drafting
                                            ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" />
                                            : <RefreshCw style={{ width: 12, height: 12 }} />}
                                        Rewrite
                                    </button>
                                )}
                                {editing ? (
                                    <>
                                        <button type="button" className="cc-btn cc-btn-primary cc-btn-sm" onClick={saveDraft} disabled={busy}>
                                            {busy ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" /> : <Check style={{ width: 12, height: 12 }} />}Save draft
                                        </button>
                                        <button type="button" className="cc-btn cc-btn-ghost cc-btn-sm" onClick={() => { setEditing(false); setSubject(action.draft?.subject || ''); setBody(action.draft?.body || ''); }}>
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <button type="button" className="cc-btn cc-btn-ghost cc-btn-sm" onClick={() => setEditing(true)} disabled={terminal}>
                                        <Pencil style={{ width: 12, height: 12 }} />Edit
                                    </button>
                                )}
                                <button type="button" className="cc-btn cc-btn-ghost cc-btn-sm" onClick={copyDraft}>
                                    <Copy style={{ width: 12, height: 12 }} />Copy
                                </button>
                                <span style={{ flex: 1 }} />
                                {action.kind === 'email' && senderEmail && (
                                    <span className="cc-tiny">Sends from {senderEmail}</span>
                                )}
                            </div>

                            {/*
                                INSIDE the draft card, not beside it.

                                Refine acts on the draft directly above it, but
                                it used to render as its own bordered, tinted
                                card — so the screen showed two sibling panels
                                and gave no clue that one was a control for the
                                other. Sharing the card's border makes the
                                relationship structural rather than something
                                the user has to infer from proximity.

                                Same affordance as My Network's modal, so the
                                skill transfers between the two screens.
                            */}
                            {!isPending && !editing && (
                                <div className="cc-refine">
                                    <div className="cc-refine-head">
                                        <Sparkles style={{ width: 13, height: 13 }} />
                                        Refine with a prompt
                                        <span className="cc-tiny" style={{ fontWeight: 500 }}>· optional</span>
                                    </div>
                                    <div className="cc-refine-row">
                                        <input
                                            className="cc-refine-input"
                                            placeholder="e.g. make this shorter and more formal"
                                            value={refinePrompt}
                                            disabled={drafting}
                                            onChange={(e) => setRefinePrompt(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') runRefine(refinePrompt); }}
                                        />
                                        <button
                                            type="button"
                                            className="cc-refine-go"
                                            disabled={drafting || !refinePrompt.trim()}
                                            onClick={() => runRefine(refinePrompt)}
                                        >
                                            {drafting
                                                ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" />
                                                : <Sparkles style={{ width: 12, height: 12 }} />}
                                            Generate
                                        </button>
                                    </div>
                                    <div className="cc-refine-chips">
                                        {['Make it shorter', 'More casual', 'Add my portfolio', 'Stronger CTA'].map((chip) => (
                                            <button key={chip} type="button" disabled={drafting} onClick={() => runRefine(chip)}>
                                                {chip}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {blocked && (
                            /* An unconnected channel is never a dead end: the draft is
                               still copy-pasteable, and the user can record that they
                               sent it themselves. */
                            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 10, background: '#FFFBEB', border: '1px solid #FDE68A', flexWrap: 'wrap' }}>
                                <AlertTriangle style={{ width: 13, height: 13, color: '#B45309', flexShrink: 0 }} />
                                <span className="cc-tiny" style={{ color: '#B45309', fontWeight: 600 }}>{blocked.text}</span>
                            </div>
                        )}
                    </div>
                )}

                {/*
                    THE ROW'S REAL VERB, not a uniform one.

                    A fetch_contact row is a purchase: Approve queues it and
                    Execute spends the credits. A message row is not — the
                    executor skips every email / linkedin_* action, so approving
                    one set a status that nothing downstream ever read. It looked
                    like a commitment and did nothing. Until a send pass exists,
                    the honest actions on a drafted message are copy it and
                    record that you sent it.

                    A message row has NO Approve and NO Skip. Neither does
                    anything a user can see today, so they were removed rather
                    than left looking like decisions. Its real verbs are Send and
                    Mark as sent. A row skipped before this change still offers
                    Undo skip, so it is never stranded.
                */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 4, flexWrap: 'wrap' }}>
                    {terminal ? (
                        <span className="cc-chip cc-chip-primary">{statusLabelFor(action)}</span>
                    ) : needs ? (
                        <>
                            <button
                                type="button"
                                className="cc-btn cc-btn-primary"
                                disabled={busy || action.status === 'approved'}
                                onClick={() => setStatus(DIGEST_ACTION_STATUSES.APPROVED)}
                            >
                                {busy ? <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" /> : <Check style={{ width: 13, height: 13 }} />}
                                {action.status === 'approved' ? 'Approved' : 'Approve'}
                            </button>
                            <button
                                type="button"
                                className="cc-btn cc-btn-secondary"
                                disabled={busy}
                                onClick={() => setStatus(action.status === 'skipped'
                                    ? DIGEST_ACTION_STATUSES.PROPOSED
                                    : DIGEST_ACTION_STATUSES.SKIPPED)}
                            >
                                {action.status === 'skipped'
                                    ? <><RotateCcw style={{ width: 13, height: 13 }} />Undo skip</>
                                    : <><X style={{ width: 13, height: 13 }} />Skip</>}
                            </button>
                        </>
                    ) : (
                        <>
                            {/*
                                No send button once the email has gone out — the
                                status chip in the draft header already says so,
                                and repeating it here said the same thing twice
                                on one card. Mark as sent stays, so the row can
                                still be filed.
                            */}
                            {priorEmailLabel ? null : needsGoogle ? (
                                <button
                                    type="button"
                                    className="cc-btn cc-btn-primary"
                                    onClick={connectGoogle}
                                    title="Sending uses your Gmail — connect once to send as yourself"
                                >
                                    <ExternalLink style={{ width: 13, height: 13 }} />
                                    Connect Google to send
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="cc-btn cc-btn-primary"
                                    disabled={busy || isPending || drafting}
                                    title={isPending ? 'The draft is still being written' : ''}
                                    onClick={async () => {
                                        setBusy(true);
                                        const sent = await dispatch(sendActionNow({ digestId, actionId: action.actionId }));
                                        setBusy(false);
                                        if (sendActionNow.fulfilled.match(sent)) dispatch(refreshDigest(digestId));
                                    }}
                                >
                                    {busy ? <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" /> : <Send style={{ width: 13, height: 13 }} />}
                                    {sendButtonLabelFor(action)}
                                </button>
                            )}
                            <button
                                type="button"
                                className="cc-btn cc-btn-ghost"
                                disabled={busy}
                                onClick={() => dispatch(markActionSent({ digestId, actionId: action.actionId }))}
                            >
                                <Check style={{ width: 13, height: 13 }} />Mark as sent
                            </button>
                            {action.status === 'skipped' && (
                                <button
                                    type="button"
                                    className="cc-btn cc-btn-ghost"
                                    disabled={busy}
                                    onClick={() => setStatus(DIGEST_ACTION_STATUSES.PROPOSED)}
                                >
                                    <RotateCcw style={{ width: 13, height: 13 }} />Undo skip
                                </button>
                            )}
                        </>
                    )}
                    <span style={{ flex: 1 }} />
                    {/*
                        The hint describes what the primary button would do. With
                        the button replaced by "Email sent", it would advertise an
                        action that is no longer on offer, so it points at the
                        invite control above instead.
                    */}
                    <span className="cc-tiny">
                        {priorEmailLabel
                            ? 'Already emailed · use the LinkedIn bar above to invite'
                            : needsGoogle
                                ? 'Sending uses your Gmail — connect once to send as yourself. You can copy the draft meanwhile.'
                                : sendHintFor(action, senderEmail)}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default DigestDetailPane;
